import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger, Button, Input, Label } from '@ideoz/ui';
import { ChevronDown } from 'lucide-react';

export type ColorPickerProps = {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  className?: string;
  /**
   * When set, renders the compact "token card" style — swatch + bold name on top
   * and the hex (`label`) muted beneath, no dropdown chevron. When omitted, the
   * original bordered button + chevron is used (kept for other call sites).
   */
  name?: string;
};

const PRESET_COLORS = [
  '#000000', '#ffffff', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#6366f1', '#8b5cf6',
  '#ec4899', '#64748b', '#1e293b', '#dc2626', '#ea580c', '#059669', '#0284c7', '#4f46e5',
];

const ColorPicker: React.FC<ColorPickerProps> = ({ label, value = '#ffffff', onChange, className, name }) => {
  const [inputValue, setInputValue] = useState(value);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handlePresetClick = (color: string) => {
    setInputValue(color);
    onChange(color);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {name ? (
          <button
            type="button"
            className={`flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-start transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800 ${className || ''}`}
          >
            <span
              className="size-8 shrink-0 rounded-md border border-gray-200 dark:border-gray-600"
              style={{ backgroundColor: value }}
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {name}
              </span>
              <span className="block truncate text-xs text-gray-400 dark:text-gray-500">
                {label}
              </span>
            </span>
          </button>
        ) : (
          <Button
            variant="secondary-gray"
            className={`w-full justify-between ${className || ''}`}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-6 h-6 rounded-sm border border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: value }}
              />
              <span className="text-sm font-normal">{label}</span>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hex-input">Hex Color</Label>
            <Input
              id="hex-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="#000000"
              className="font-mono"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Preset Colors</Label>
            <div className="grid grid-cols-8 gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className="w-6 h-6 rounded border-2 border-gray-200 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ backgroundColor: color }}
                  onClick={() => handlePresetClick(color)}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="native-picker">Custom Color</Label>
            <input
              id="native-picker"
              type="color"
              value={value}
              onChange={(e) => {
                setInputValue(e.target.value);
                onChange(e.target.value);
              }}
              className="w-full h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColorPicker;
